import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { StorefrontPreview } from "@/modules/ecommerce/components/StorefrontPreview";
import { StorefrontPasswordGate } from "@/modules/ecommerce/components/StorefrontPasswordGate";
import {
  DEFAULT_SECTIONS,
  isStorefrontGateUnlocked,
  mergeStorefrontTheme,
  normalizeSections,
  verifyStorefrontGatePublic,
  type StorefrontSectionRow,
} from "@/modules/ecommerce/services/storefront-web-service";
import {
  fetchPublicStorefrontProducts,
  fetchPublicStorefrontCategories,
} from "../services/public-storefront-service";
import {
  StoreUnavailableScreen,
  StoreMaintenanceScreen,
  StoreComingSoonScreen,
  StoreErrorScreen,
} from "../components/StatusScreens";
import type { HostOrgRow } from "../contexts/HostOrganizationProvider";

interface StorefrontHomeProps {
  row: HostOrgRow;
  notFound?: boolean;
}

export function StorefrontHome({ row, notFound = false }: StorefrontHomeProps) {
  const orgId = row.organization_id;
  const orgName = (row.organization_name || "Store").trim() || "Store";

  const [gateBypass, setGateBypass] = useState(false);

  const sections: StorefrontSectionRow[] =
    Array.isArray(row.storefront_sections) && row.storefront_sections.length > 0
      ? normalizeSections(row.storefront_sections)
      : [...DEFAULT_SECTIONS];

  const sectionVisibility = {
    hero: sections.find((s) => s.id === "hero")?.active !== false,
    features: sections.find((s) => s.id === "features")?.active !== false,
    products:
      sections.find((s) => s.type === "Featured Products" || s.id === "products")
        ?.active !== false,
    footer: sections.find((s) => s.id === "footer")?.active !== false,
  };

  const resolvedTheme = useMemo(
    () =>
      mergeStorefrontTheme(
        {
          store_name: row.store_name || orgName,
          store_email: "",
          store_address: "",
          store_description: "",
          store_website: "",
          store_phone: "",
        },
        row.storefront_theme ?? {},
      ),
    [row.storefront_theme, row.store_name, orgName],
  );

  // Document title + robots — driven by the resolved org row.
  useEffect(() => {
    const allowIndexing = row.storefront_allow_search_indexing !== false;
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!allowIndexing) {
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.setAttribute("name", "robots");
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute("content", "noindex, nofollow");
    } else if (robotsMeta) {
      robotsMeta.setAttribute("content", "index, follow");
    }
    document.title = notFound
      ? `Page not found — ${resolvedTheme.storeDisplayName}`
      : `${resolvedTheme.storeDisplayName} — Shop`;
  }, [
    row.storefront_allow_search_indexing,
    resolvedTheme.storeDisplayName,
    notFound,
  ]);

  useEffect(() => {
    if (orgId && isStorefrontGateUnlocked(orgId)) setGateBypass(true);
  }, [orgId]);

  // React Query owns the catalog fetch lifecycle. Both queries share the
  // same enabled gate so we don't fire RPCs while the gate is up or while
  // the storefront is offline / under maintenance.
  const isLive =
    !!orgId &&
    row.storefront_online !== false &&
    !row.storefront_maintenance &&
    (!row.storefront_password_enabled ||
      gateBypass ||
      isStorefrontGateUnlocked(orgId));

  const productsQuery = useQuery({
    queryKey: ["storefront", "catalog", orgId],
    queryFn: () => fetchPublicStorefrontProducts(orgId, 1, 20),
    enabled: isLive,
  });
  const categoriesQuery = useQuery({
    queryKey: ["storefront", "categories", orgId],
    queryFn: () => fetchPublicStorefrontCategories(orgId),
    enabled: isLive,
  });

  // Status precedence: offline → maintenance → password gate → loading
  //                  → fetch error → empty (coming soon) → live.
  if (row.storefront_online === false) return <StoreUnavailableScreen />;
  if (row.storefront_maintenance)
    return <StoreMaintenanceScreen message={row.storefront_maintenance_message} />;

  const needGate =
    row.storefront_password_enabled && !gateBypass && !isStorefrontGateUnlocked(orgId);
  if (needGate) {
    return (
      <StorefrontPasswordGate
        organizationId={orgId}
        storeName={resolvedTheme.storeDisplayName}
        onUnlocked={() => setGateBypass(true)}
        verify={(pw) => verifyStorefrontGatePublic(orgId, pw)}
      />
    );
  }

  if (productsQuery.isLoading || categoriesQuery.isLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center space-y-4">
        <Loader2 className="h-12 w-12 text-indigo-600 animate-spin" />
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
          Loading storefront…
        </p>
      </div>
    );
  }

  // Fetch failure (Supabase outage, RPC mis-deploy, etc.) is a different
  // signal from "this org has no published products". Render the generic
  // error screen for the former so the visitor can retry, and the
  // explicit "coming soon" copy for the latter.
  if (productsQuery.isError || categoriesQuery.isError) {
    return (
      <StoreErrorScreen
        message={
          (productsQuery.error as Error | undefined)?.message ||
          (categoriesQuery.error as Error | undefined)?.message
        }
        onRetry={() => {
          productsQuery.refetch();
          categoriesQuery.refetch();
        }}
      />
    );
  }

  const products = productsQuery.data?.products ?? [];
  const categories = (categoriesQuery.data ?? []).length
    ? (categoriesQuery.data as string[])
    : ["All products"];

  // Genuinely empty catalogue — show the "coming soon" message instead
  // of fabricating demo data.
  if (products.length === 0) {
    return <StoreComingSoonScreen name={resolvedTheme.storeDisplayName} />;
  }

  return (
    <div className="w-full min-h-screen overflow-x-hidden bg-white">
      <StorefrontPreview
        categories={categories}
        featuredProducts={products}
        viewMode="desktop"
        theme={resolvedTheme}
        // Stock is already snapshotted into each product at fetch time
        // (see fetchPublicStorefrontProducts → product.stock). The
        // `get_ecommerce_product_availability` RPC requires a JWT-bound
        // organization claim and is not anon-callable; the anon
        // storefront uses the snapshot value instead.
        skipStockCheck={true}
        sectionVisibility={sectionVisibility}
        allowBackorders={false}
        enableWishlist={false}
        enableProductComparison={false}
      />
      {notFound ? (
        <div className="bg-slate-50 border-t border-slate-200 text-slate-700 text-center text-xs py-2 px-4">
          The page you tried to reach doesn't exist on this store. Browse the catalogue above.
        </div>
      ) : null}
    </div>
  );
}
